import React from 'react'
import type { CacOrder } from 'shared/types'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { H3, H4, Text } from 'app/common/typography'
import {
    formatDateToDayMonthYear,
    formatDateToHoursAndMinutes,
    formatPrice,
    isDefined
} from 'shared/utils'
import _ from 'lodash'
import { ecommOrderToCart } from 'shared/store/ecommerce/catalog'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import { CartItem } from 'app/ecommerce/common/components/cart-summary'
import { colors } from 'shared/assets/styles'
import { sumProductFinalPrices } from 'shared/utils/ecommerce/utils'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { DownloadReceiptButton } from '../download-receipt'

const { lightGrey } = colors
type Props = {
    order?: CacOrder
}
const OrderDetails = (props: Props) => {
    const { order } = props
    if (!order) {
        return null
    }
    const { originalPrice, receiptAvailable } = order
    const purchasables = ecommOrderToCart(order)
    const products = _.groupBy(purchasables, 'categoryName')
    const priceOriginal = purchasables
        ? sumProductFinalPrices(purchasables)
        : originalPrice
    const renderProducts = () => {
        if (!products) {
            return null
        }
        return Object.entries(products).map(
            (
                [
                    categoryName: string,
                    purchasableItems: Array<PurchasableItem>
                ],
                index
            ) => {
                return (
                    <div key={index}>
                        <H3>{categoryName}</H3>
                        {/*//$FlowFixMe*/}
                        {purchasableItems.map((item, index) => {
                            return (
                                <Item key={index}>
                                    <CartItem
                                        product={item}
                                        fromOrderDetails={true}
                                    />
                                </Item>
                            )
                        })}
                    </div>
                )
            }
        )
    }
    const {
        orderNumber,
        restaurant,
        finalPrice,
        promotions,
        creationDate,
        id
    } = order
    return (
        <StyledResponsiveContainer>
            <TopContainer>
                {orderNumber && (
                    <OrderContainer>
                        <FormattedMessage id="clickandcollect.listorders.ordernumber">
                            {(message: string) => <Title>{message}</Title>}
                        </FormattedMessage>
                        <Title textTransform={'uppercase'}>{orderNumber}</Title>
                    </OrderContainer>
                )}
                <FormattedMessage
                    id="ecommerce.order.recap.date"
                    values={{
                        date: formatDateToDayMonthYear(creationDate),
                        time: formatDateToHoursAndMinutes(creationDate)
                    }}
                />
                {restaurant && <Title>{restaurant.name}</Title>}
                <PriceContainer>
                    <FormattedMessage id="clickandcollect.listorders.total">
                        {(message: string) => <Title>{message}</Title>}
                    </FormattedMessage>
                    {isDefined(finalPrice) && (
                        //$FlowFixMe
                        <Title>{formatPrice(finalPrice)}</Title>
                    )}
                </PriceContainer>
            </TopContainer>
            {renderProducts()}
            {isDefined(priceOriginal) && (
                <Line>
                    <FormattedMessage id="clickandcollect.cart.subtotal">
                        {(text: string) => <Text>{text}</Text>}
                    </FormattedMessage>
                    {/*//$FlowFixMe*/}
                    <Text>{formatPrice(priceOriginal)}</Text>
                </Line>
            )}
            {promotions &&
                promotions.length > 0 &&
                promotions.map((promotion, index) => (
                    <Line key={index}>
                        <Text>{promotion.promotionName}</Text>
                        {priceOriginal && finalPrice && (
                            <Text>
                                {formatPrice(finalPrice - priceOriginal)}
                            </Text>
                        )}
                    </Line>
                ))}
            {isDefined(finalPrice) && (
                <Line last>
                    <FormattedMessage id="clickandcollect.cart.total">
                        {(text: string) => <H4>{text}</H4>}
                    </FormattedMessage>
                    {/*//$FlowFixMe*/}
                    <H4>{formatPrice(finalPrice)}</H4>
                </Line>
            )}
            {receiptAvailable && (
                <DownloadReceiptButton orderId={id} isDelivery={false} />
            )}
        </StyledResponsiveContainer>
    )
}
const StyledResponsiveContainer = styled(ResponsiveContainer)`
    ${media.medium`
        width: 450px;
    `}
`

const TopContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 15px;
`

const OrderContainer = styled.div`
    display: flex;
    margin-bottom: 20px;
`

const Title = styled(H4)`
    text-align: center;
    margin-top: 10px;
    ${props => props.textTransform && `text-transform: ${props.textTransform}`};
`

const PriceContainer = styled.div`
    display: flex;
`

const Item = styled.div`
    margin: 8px 0;
`

const Line = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 20px 0;
    border: 0.5px solid ${lightGrey};
    border-right-width: 0;
    border-left-width: 0;
    ${props => props.last && `margin-bottom: 50px;`}
`
export { OrderDetails }
